import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Check,
  ChevronDown,
  ExternalLink,
  Globe,
  Pencil,
  Plus,
  RotateCw,
  Scale,
  Trash2,
  X,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { crmApi } from "./api";
import { ConfirmModal } from "../../components/ui/confirm-modal";
import { ToggleSwitch } from "../../components/ui/toggle-switch";
import { Dropdown } from "../../components/ui/dropdown";
import { useToast } from "../../components/ui/toast";
import {
  ASSIGNMENT_MODES,
  ASSIGNMENT_MODE_DESCRIPTIONS,
  ASSIGNMENT_MODE_LABELS,
  LEAD_SOURCES,
  type AssignmentMode,
  type AssignmentRule,
  type CrmMember,
} from "./types";

const labelCls = "mb-1.5 block text-xs font-semibold text-ink dark:text-stone-200";

const MODE_ICONS: Record<AssignmentMode, any> = {
  round_robin: RotateCw,
  source: Globe,
  load_balanced: Scale,
};

function SourceCombobox({
  value,
  onChange,
  placeholder = "Source (e.g. Website, Meta Ads)",
}: {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const presets = useMemo(() => {
    return Array.from(new Set([...LEAD_SOURCES, "Meta Ads", "Google Ads", "Inbound Call"]));
  }, []);

  const filtered = useMemo(() => {
    if (!value) return presets;
    return presets.filter((p) => p.toLowerCase().includes(value.toLowerCase()));
  }, [presets, value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative flex-1 min-w-[180px]">
      <div className="relative">
        <input
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="w-full rounded-lg border border-line dark:border-white/15 bg-white dark:bg-stone-800 px-3 py-2 text-xs text-ink dark:text-stone-100 outline-none focus:border-brand pr-8"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setOpen((prev) => !prev)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-3 dark:text-stone-400 hover:text-ink dark:hover:text-white"
        >
          <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
        </button>
      </div>

      {open && (
        <div className="absolute left-0 top-full mt-1 w-full rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#201e1b] shadow-lg py-1 z-50 max-h-48 overflow-y-auto">
          <div className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-ink-3 dark:text-stone-400">
            Suggested Sources
          </div>
          {filtered.length === 0 ? (
            <div className="px-3 py-1.5 text-xs text-ink-3 dark:text-stone-400">
              Custom source: &ldquo;{value}&rdquo;
            </div>
          ) : (
            filtered.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => {
                  onChange(preset);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center justify-between px-3 py-1.5 text-left text-xs transition-colors hover:bg-stone-100 dark:hover:bg-stone-800",
                  value.toLowerCase() === preset.toLowerCase()
                    ? "font-semibold text-brand bg-orange-50/50 dark:bg-orange-950/20"
                    : "text-ink dark:text-stone-200",
                )}
              >
                <span>{preset}</span>
                {value.toLowerCase() === preset.toLowerCase() && (
                  <Check className="h-3 w-3 text-brand" />
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function RuleForm({
  title,
  subtitle,
  initial,
  agents,
  onSave,
  saving,
  error,
  onCancel,
}: {
  title?: string;
  subtitle?: string;
  initial: { mode: AssignmentMode; agentIds: string[]; sourceMap: Record<string, string>; active: boolean };
  agents: CrmMember[];
  onSave: (data: { mode: AssignmentMode; agentIds: string[]; sourceMap: Record<string, string>; active: boolean }) => Promise<void>;
  saving: boolean;
  error: string;
  onCancel?: () => void;
}) {
  const [mode, setMode] = useState<AssignmentMode>(initial.mode);
  const [agentIds, setAgentIds] = useState<string[]>(initial.agentIds);
  const [sourceRows, setSourceRows] = useState<[string, string][]>(Object.entries(initial.sourceMap));
  const [active, setActive] = useState(initial.active);

  const toggleAgent = (id: string) =>
    setAgentIds((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));

  const submit = async () => {
    const sourceMap: Record<string, string> = {};
    for (const [source, agent] of sourceRows) {
      const s = source.trim();
      if (s && agent) sourceMap[s] = agent;
    }
    await onSave({ mode, agentIds, sourceMap, active });
  };

  return (
    <div className="space-y-4">
      {/* 1. Header with Status, Switch, and Save */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line dark:border-white/10 pb-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h2 className="text-sm font-bold text-ink dark:text-white">{title || "Default rule"}</h2>
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium border",
                active
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800/50"
                  : "bg-stone-100 text-stone-600 border-stone-200 dark:bg-stone-800 dark:text-stone-400 dark:border-stone-700",
              )}
            >
              <span className={cn("h-1.5 w-1.5 rounded-full", active ? "bg-emerald-500" : "bg-stone-400")} />
              {active ? "Active" : "Paused"}
            </span>
          </div>
          {subtitle && (
            <p className="mt-0.5 text-xs text-ink-3 dark:text-stone-400">{subtitle}</p>
          )}
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-3 dark:text-stone-400">
              {active ? "Routing active" : "Paused"}
            </span>
            <ToggleSwitch checked={active} onChange={setActive} ariaLabel="Rule active toggle" />
          </div>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-line dark:border-white/15 px-3 py-1.5 text-xs font-semibold text-ink-2 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            disabled={saving}
            onClick={() => void submit()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-brand-hover active:scale-[0.98] transition-all disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
            <span>{saving ? "Saving…" : "Save rule"}</span>
          </button>
        </div>
      </div>

      {/* 2. Routing Strategy Cards */}
      <div>
        <label className={labelCls}>Routing strategy</label>
        <div className="grid gap-3 sm:grid-cols-3">
          {ASSIGNMENT_MODES.map((m) => {
            const Icon = MODE_ICONS[m];
            const isSelected = mode === m;
            return (
              <div
                key={m}
                onClick={() => setMode(m)}
                className={cn(
                  "group cursor-pointer rounded-xl border p-3.5 transition-all",
                  isSelected
                    ? "border-brand bg-orange-50/50 dark:bg-orange-950/20 shadow-xs ring-1 ring-brand/30"
                    : "border-line dark:border-white/10 bg-white dark:bg-stone-900/30 hover:border-stone-300 dark:hover:border-white/20",
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <div
                      className={cn(
                        "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors",
                        isSelected
                          ? "bg-brand text-white shadow-xs"
                          : "bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 group-hover:text-ink dark:group-hover:text-white",
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <p className="text-xs font-bold text-ink dark:text-white">{ASSIGNMENT_MODE_LABELS[m]}</p>
                  </div>
                  <div
                    className={cn(
                      "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors mt-0.5",
                      isSelected ? "border-brand bg-brand" : "border-stone-300 dark:border-stone-600 bg-transparent",
                    )}
                  >
                    {isSelected && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
                  </div>
                </div>
                <p className="mt-2 text-[11px] text-ink-3 dark:text-stone-400 leading-normal">
                  {ASSIGNMENT_MODE_DESCRIPTIONS[m]}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* 3. Eligible Agents Multi-Selector */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <label className="text-xs font-semibold text-ink dark:text-stone-200">
            Eligible sales reps{" "}
            <span className="font-normal text-ink-3 dark:text-stone-400">
              {agentIds.length === 0
                ? "(All active sales reps eligible by default)"
                : `(${agentIds.length} of ${agents.length} reps selected)`}
            </span>
          </label>
          {agents.length > 1 && (
            <div className="flex items-center gap-2 text-xs">
              <button
                type="button"
                onClick={() => setAgentIds(agents.map((a) => a.id))}
                className="text-brand hover:underline font-medium text-[11px]"
              >
                Select all
              </button>
              <span className="text-stone-300 dark:text-stone-600">·</span>
              <button
                type="button"
                onClick={() => setAgentIds([])}
                className="text-ink-3 dark:text-stone-400 hover:text-ink dark:hover:text-white font-medium text-[11px]"
              >
                Reset (All active)
              </button>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {agents.map((a) => {
            const isSelected = agentIds.includes(a.id);
            const initials = a.name
              .split(" ")
              .map((p) => p[0])
              .join("")
              .slice(0, 2)
              .toUpperCase();

            return (
              <button
                key={a.id}
                type="button"
                onClick={() => toggleAgent(a.id)}
                className={cn(
                  "inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium border transition-all",
                  isSelected
                    ? "border-brand bg-orange-50 text-brand dark:bg-orange-950/30 dark:border-brand/40 shadow-xs font-semibold"
                    : "border-line dark:border-white/10 bg-white dark:bg-stone-800 text-ink-2 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700/60",
                )}
              >
                <span
                  className={cn(
                    "flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white",
                    isSelected ? "bg-brand" : "bg-stone-400 dark:bg-stone-600",
                  )}
                >
                  {initials}
                </span>
                <span>{a.name}</span>
                {isSelected && <Check className="h-3 w-3 text-brand" />}
              </button>
            );
          })}
          {agents.length === 0 && (
            <p className="text-xs text-ink-3 dark:text-stone-400">No active eligible sales reps found.</p>
          )}
        </div>
      </div>

      {/* 4. Source Routing (When By Source is Selected) */}
      {mode === "source" && (
        <div className="rounded-xl border border-line dark:border-white/10 bg-stone-50/70 dark:bg-stone-900/20 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-xs font-semibold text-ink dark:text-stone-200">
                Source to Agent Routing
              </label>
              <p className="text-[11px] text-ink-3 dark:text-stone-400">
                Map specific lead acquisition channels to dedicated reps
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSourceRows((prev) => [...prev, ["", ""]])}
              className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:text-brand-hover"
            >
              <Plus className="h-3.5 w-3.5" />
              Add mapping
            </button>
          </div>

          {sourceRows.length === 0 ? (
            <div className="rounded-lg border border-dashed border-line dark:border-white/10 p-4 text-center text-xs text-ink-3 dark:text-stone-400">
              No specific sources mapped yet. Leads from unmapped sources fall back to round robin.
            </div>
          ) : (
            <div className="space-y-2.5">
              {sourceRows.map(([source, agent], i) => (
                <div key={i} className="flex items-center gap-2">
                  <SourceCombobox
                    value={source}
                    onChange={(val) =>
                      setSourceRows((prev) =>
                        prev.map((row, j) => (j === i ? [val, row[1]] : row)),
                      )
                    }
                  />
                  <span className="text-stone-400 text-xs shrink-0 font-bold">→</span>
                  <div className="flex-1 min-w-[160px]">
                    <Dropdown
                      ariaLabel="Designated rep"
                      value={agent}
                      onChange={(val) =>
                        setSourceRows((prev) =>
                          prev.map((row, j) => (j === i ? [row[0], val] : row)),
                        )
                      }
                      className="w-full text-xs py-2 bg-white dark:bg-stone-800 border-line dark:border-white/15 text-ink dark:text-stone-100"
                      options={[
                        { value: "", label: "Choose designated rep…" },
                        ...agents.map((a) => ({ value: a.id, label: a.name })),
                      ]}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setSourceRows((prev) => prev.filter((_, j) => j !== i))}
                    aria-label="Remove mapping"
                    className="shrink-0 rounded-lg p-2 text-ink-3 hover:bg-rose-50 dark:hover:bg-rose-950/30 hover:text-rose-600 transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {error && <p role="alert" className="text-xs font-semibold text-rose-500">{error}</p>}
    </div>
  );
}

export function AssignmentPage() {
  const cache = useQueryClient();
  const toast = useToast();
  const team = useQuery({ queryKey: ["crm-team"], queryFn: crmApi.team });
  const campaigns = useQuery({ queryKey: ["crm-campaigns"], queryFn: crmApi.campaigns });
  const rules = useQuery({ queryKey: ["crm-assignment-rules"], queryFn: crmApi.assignmentRules });
  const leads = useQuery({ queryKey: ["crm-leads"], queryFn: crmApi.leads });

  const canManage = team.data?.role === "admin" || !!team.data?.permissions?.assign_leads;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [overrideCampaign, setOverrideCampaign] = useState("");
  const [pendingDelete, setPendingDelete] = useState<AssignmentRule | null>(null);

  const agents = (team.data?.members ?? []).filter((m) => m.active && m.role !== "admin" && m.role !== "");
  const unassignedCount = (leads.data ?? []).filter((l) => !l.assignedAgentId).length;

  const refresh = () => cache.invalidateQueries({ queryKey: ["crm-assignment-rules"] });

  const saveRule =
    (campaignId: string | null) =>
    async (data: {
      mode: AssignmentMode;
      agentIds: string[];
      sourceMap: Record<string, string>;
      active: boolean;
    }) => {
      if (saving) return;
      setSaving(true);
      setError("");
      try {
        await crmApi.upsertAssignmentRule({ campaignId, ...data });
        await refresh();
        toast.success("Assignment rule saved");
        if (campaignId) {
          setOverrideCampaign("");
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Unable to save rule.");
      } finally {
        setSaving(false);
      }
    };

  const removeOverride = async () => {
    if (!pendingDelete) return;
    try {
      await crmApi.deleteAssignmentRule(pendingDelete.id);
      await refresh();
      setPendingDelete(null);
      if (
        overrideCampaign &&
        !rules.data?.some((r) => r.campaignId === overrideCampaign && r.id !== pendingDelete.id)
      ) {
        setOverrideCampaign("");
      }
      toast.success("Override removed");
    } catch (e) {
      toast.danger("Couldn't delete rule", e instanceof Error ? e.message : "Unable to delete.");
      setPendingDelete(null);
    }
  };

  const defaultRule = rules.data?.find((r) => r.campaignId === null);
  const overrides = (rules.data ?? []).filter((r) => r.campaignId !== null);
  const campaignName = (id: string | null) =>
    id ? campaigns.data?.find((c) => c.id === id)?.name ?? id : "Default (all campaigns)";
  const selectedOverride = overrides.find((r) => r.campaignId === overrideCampaign);
  const customizable = (campaigns.data ?? []).filter((c) => !overrides.some((r) => r.campaignId === c.id));

  return (
    <div className="flex h-full flex-col p-4 sm:p-6 space-y-4">
      {/* 1. Header with Unassigned Leads Badge */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-base font-semibold text-ink dark:text-white">Assignment</h1>

        {/* Operational Glance: Unassigned Queue */}
        <Link
          to="/crm/leads"
          className="inline-flex items-center gap-2 rounded-lg border border-line dark:border-white/10 bg-elevated dark:bg-stone-800 px-3 py-1.5 text-xs font-medium text-ink-2 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors"
          title="View unassigned leads"
        >
          <span
            className={cn(
              "h-2 w-2 rounded-full",
              unassignedCount > 0 ? "bg-amber-500 animate-pulse" : "bg-emerald-500",
            )}
          />
          <span>
            {unassignedCount} unassigned lead{unassignedCount === 1 ? "" : "s"}
          </span>
          <ExternalLink className="h-3 w-3 text-stone-400" />
        </Link>
      </div>

      {!canManage && team.data ? (
        <div className="rounded-xl border border-line dark:border-white/10 bg-elevated dark:bg-[#201e1b] p-6 text-sm text-ink-2 dark:text-stone-300">
          Assignment rules are managed by sales managers and business admins.
        </div>
      ) : (
        <>
          {rules.error && (
            <p role="alert" className="text-xs font-semibold text-rose-500">
              {(rules.error as Error).message}
            </p>
          )}

          {/* 2. Default Rule Card (Option 1 Header-Integrated) */}
          <section className="rounded-xl border border-line dark:border-white/10 bg-elevated dark:bg-[#201e1b] p-5 shadow-xs">
            {defaultRule ? (
              <RuleForm
                key={defaultRule.id + String(defaultRule.updatedAt)}
                title="Default Routing Rule"
                subtitle="Applies automatically to incoming leads from every campaign without a custom override."
                initial={{
                  mode: defaultRule.mode,
                  agentIds: defaultRule.agentIds,
                  sourceMap: defaultRule.sourceMap,
                  active: defaultRule.active,
                }}
                agents={agents}
                onSave={saveRule(null)}
                saving={saving}
                error={error}
              />
            ) : (
              <RuleForm
                key="new-default"
                title="Default Routing Rule"
                subtitle="Applies automatically to incoming leads from every campaign without a custom override."
                initial={{ mode: "round_robin", agentIds: [], sourceMap: {}, active: true }}
                agents={agents}
                onSave={saveRule(null)}
                saving={saving}
                error={error}
              />
            )}
          </section>

          {/* 3. Campaign Overrides Section */}
          <section className="rounded-xl border border-line dark:border-white/10 bg-elevated dark:bg-[#201e1b] p-5 shadow-xs space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-bold text-ink dark:text-white">Campaign overrides</h2>
                <p className="mt-0.5 text-xs text-ink-3 dark:text-stone-400">
                  Custom routing rules configured for specific campaigns take priority over the default rule.
                </p>
              </div>

              {customizable.length > 0 && (
                <div className="w-64">
                  <Dropdown
                    ariaLabel="Configure campaign override"
                    value={overrideCampaign}
                    onChange={setOverrideCampaign}
                    className="w-full text-xs py-2 bg-stone-100 dark:bg-stone-800 border-line dark:border-white/15 text-ink dark:text-stone-200"
                    options={[
                      { value: "", label: "+ Configure campaign override…" },
                      ...customizable.map((c) => ({ value: c.id, label: c.name })),
                    ]}
                  />
                </div>
              )}
            </div>

            {/* Overrides Table */}
            {overrides.length > 0 ? (
              <div className="overflow-x-auto rounded-xl border border-line dark:border-white/10 bg-white dark:bg-stone-900/30">
                <table className="w-full text-left text-sm">
                  <thead className="bg-stone-100 dark:bg-stone-800/80 text-[11px] font-semibold uppercase tracking-wider text-ink-2 dark:text-stone-300 border-b border-line dark:border-white/10">
                    <tr>
                      <th className="px-4 py-2.5">Campaign</th>
                      <th className="px-4 py-2.5">Strategy</th>
                      <th className="px-4 py-2.5">Eligible Reps</th>
                      <th className="px-4 py-2.5">Status</th>
                      <th className="px-4 py-2.5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line dark:divide-white/10 text-xs">
                    {overrides.map((r) => (
                      <tr key={r.id} className="hover:bg-stone-50 dark:hover:bg-stone-800/40 transition-colors">
                        <td className="px-4 py-3 font-semibold text-ink dark:text-stone-100">
                          {campaignName(r.campaignId)}
                        </td>
                        <td className="px-4 py-3 text-ink-2 dark:text-stone-300">
                          <span className="font-medium">{ASSIGNMENT_MODE_LABELS[r.mode]}</span>
                        </td>
                        <td className="px-4 py-3 text-ink-3 dark:text-stone-400">
                          {r.agentIds.length > 0
                            ? `${r.agentIds.length} rep${r.agentIds.length === 1 ? "" : "s"}`
                            : "All active"}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={cn(
                              "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium border",
                              r.active
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800/50"
                                : "bg-stone-100 text-stone-600 border-stone-200 dark:bg-stone-800 dark:text-stone-400 dark:border-stone-700",
                            )}
                          >
                            <span
                              className={cn(
                                "h-1.5 w-1.5 rounded-full",
                                r.active ? "bg-emerald-500" : "bg-stone-400",
                              )}
                            />
                            {r.active ? "Active" : "Paused"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => setOverrideCampaign(r.campaignId || "")}
                              className="rounded-lg p-1.5 text-ink-3 hover:bg-stone-100 dark:hover:bg-stone-800 hover:text-ink dark:hover:text-white transition-colors"
                              title="Edit override rule"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setPendingDelete(r)}
                              className="rounded-lg p-1.5 text-ink-3 hover:bg-rose-50 dark:hover:bg-rose-950/30 hover:text-rose-600 transition-colors"
                              title="Delete override"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-line dark:border-white/10 p-6 text-center text-xs text-ink-3 dark:text-stone-400">
                No campaign overrides created. All campaigns currently use the default rule above.
              </div>
            )}

            {/* Active Override Editor Panel */}
            {overrideCampaign && (
              <div className="mt-4 rounded-xl border border-brand/30 bg-orange-50/15 dark:bg-orange-950/10 p-4">
                {selectedOverride ? (
                  <RuleForm
                    key={selectedOverride.id + String(selectedOverride.updatedAt)}
                    title={`Configuring Override: ${campaignName(overrideCampaign)}`}
                    subtitle="Custom routing rule applied to leads from this campaign only."
                    initial={{
                      mode: selectedOverride.mode,
                      agentIds: selectedOverride.agentIds,
                      sourceMap: selectedOverride.sourceMap,
                      active: selectedOverride.active,
                    }}
                    agents={agents}
                    onSave={saveRule(selectedOverride.campaignId)}
                    saving={saving}
                    error={error}
                    onCancel={() => setOverrideCampaign("")}
                  />
                ) : (
                  <RuleForm
                    key={`new-${overrideCampaign}`}
                    title={`Configuring Override: ${campaignName(overrideCampaign)}`}
                    subtitle="Custom routing rule applied to leads from this campaign only."
                    initial={{ mode: "round_robin", agentIds: [], sourceMap: {}, active: true }}
                    agents={agents}
                    onSave={saveRule(overrideCampaign)}
                    saving={saving}
                    error={error}
                    onCancel={() => setOverrideCampaign("")}
                  />
                )}
              </div>
            )}
          </section>
        </>
      )}

      {/* Delete Override Confirmation Modal */}
      {pendingDelete && (
        <ConfirmModal
          title="Delete rule override"
          body={`Delete the custom assignment rule for "${campaignName(pendingDelete.campaignId)}"? Incoming leads for this campaign will revert to the default assignment rule.`}
          confirmLabel="Delete rule"
          onConfirm={() => void removeOverride()}
          onClose={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}
